import React, { useState } from 'react';
import {
    TotpMultiFactorGenerator,
} from 'firebase/auth';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/lab/Alert';
import API from '../../Util/api';

const MFAChallengeDialog = ({ open, resolver, email, onSuccess, onCancel }) => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Recovery flow state
    const [showRecovery, setShowRecovery] = useState(false);
    const [recoveryEmail, setRecoveryEmail] = useState(email || '');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [recoveryLoading, setRecoveryLoading] = useState(false);
    const [recoverySuccess, setRecoverySuccess] = useState(false);
    const [recoveryError, setRecoveryError] = useState('');

    const handleSubmit = async () => {
        if (code.length !== 6) {
            setError('Please enter a 6-digit code.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const totpHint = resolver.hints.find(
                (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
            );

            if (!totpHint) {
                setError('No authenticator app factor found. Please contact support.');
                setIsLoading(false);
                return;
            }

            const assertion = TotpMultiFactorGenerator.assertionForSignIn(
                totpHint.uid,
                code
            );

            const userCredential = await resolver.resolveSignIn(assertion);
            onSuccess(userCredential);
        } catch (err) {
            console.error('MFA verification error:', err);
            if (err.code === 'auth/invalid-verification-code') {
                setError('Invalid code. Please check your authenticator app and try again.');
            } else {
                setError(err.message || 'Verification failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setCode('');
        setError('');
        setShowRecovery(false);
        setRecoveryEmail(email || '');
        setRecoveryCode('');
        setRecoveryError('');
        setRecoverySuccess(false);
        onCancel();
    };

    const handleVerifyRecoveryCode = async () => {
        if (!recoveryCode.trim()) {
            setRecoveryError('Please enter a recovery code.');
            return;
        }
        if (!recoveryEmail) {
            setRecoveryError('Please enter your email address.');
            return;
        }

        setRecoveryLoading(true);
        setRecoveryError('');

        try {
            await API.post('/user/mfa-reset/recovery', {
                email: recoveryEmail,
                recoveryCode: recoveryCode.trim(),
            });
            setRecoverySuccess(true);
        } catch (err) {
            console.error('Error verifying recovery code:', err);
            if (err.response?.status === 400) {
                setRecoveryError('Invalid recovery code. Please try again.');
            } else {
                setRecoveryError('Something went wrong. Please try again.');
            }
        } finally {
            setRecoveryLoading(false);
        }
    };

    const handleBackToCode = () => {
        setShowRecovery(false);
        setRecoveryEmail(email || '');
        setRecoveryCode('');
        setRecoveryError('');
    };

    // Recovery success view
    if (recoverySuccess) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>MFA Reset Complete</DialogTitle>
                <DialogContent>
                    <Alert severity="success" style={{ marginBottom: 16 }}>
                        Multi-factor authentication has been reset successfully.
                    </Alert>
                    <Typography variant="body2">
                        Please sign in again. You will no longer be prompted for an authenticator code.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={handleClose}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // Recovery flow view
    if (showRecovery) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>Use Recovery Code</DialogTitle>
                <DialogContent>
                    {recoveryError && (
                        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setRecoveryError('')}>
                            {recoveryError}
                        </Alert>
                    )}

                    <Typography variant="body2" style={{ marginBottom: 16 }}>
                        Enter one of the recovery codes you saved when you set up MFA.
                    </Typography>

                    {!email && (
                        <TextField
                            fullWidth
                            label="Email address"
                            value={recoveryEmail}
                            onChange={(e) => setRecoveryEmail(e.target.value.trim())}
                            type="email"
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <TextField
                        fullWidth
                        label="Recovery code"
                        value={recoveryCode}
                        onChange={(e) => setRecoveryCode(e.target.value)}
                        placeholder="xxxx-xxxx"
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && recoveryCode.trim()) {
                                handleVerifyRecoveryCode();
                            }
                        }}
                        style={{ marginBottom: 8 }}
                    />

                    <Typography variant="body2" color="textSecondary">
                        After resetting, you can sign in without an authenticator code and optionally re-enroll.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBackToCode} disabled={recoveryLoading}>
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleVerifyRecoveryCode}
                        disabled={recoveryLoading || !recoveryCode.trim() || !recoveryEmail}
                    >
                        {recoveryLoading ? <CircularProgress size={24} /> : 'Reset MFA'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // Default TOTP code entry view
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Multi-Factor Authentication</DialogTitle>
            <DialogContent>
                <Typography variant="body2" style={{ marginBottom: 16 }}>
                    Enter the 6-digit code from your authenticator app.
                </Typography>

                {error && (
                    <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    value={code}
                    onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setCode(val);
                    }}
                    placeholder="000000"
                    inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                    autoFocus
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && code.length === 6) {
                            handleSubmit();
                        }
                    }}
                    style={{ marginBottom: 16 }}
                />

                <Link
                    component="button"
                    variant="body2"
                    onClick={() => setShowRecovery(true)}
                    style={{ display: 'block', textAlign: 'center' }}
                >
                    Can't access your authenticator?
                </Link>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isLoading || code.length !== 6}
                >
                    {isLoading ? <CircularProgress size={24} /> : 'Verify'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MFAChallengeDialog;
